import React, { useState, useEffect } from "react";
import StudentDashboardLayout from "./StudentDashboardLayout";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getStudentFeeData, getStudentReceipts } from "../../utils/Api";
import { payStudentFees } from "../../utils/RazorpayCheckout";
import RazorpayStudentFeeForm from "../../RazorpayStudentFeeForm";

import PrintableReceipt from "../../components/PrintableReceipt";

// Download receipt as PDF or image
const downloadReceipt = (receipt) => {
  // Find the receipt modal or create a hidden printable area
  // Use window.print for now, or use html2canvas/jsPDF for advanced PDF
  // For demo, just open the modal and ask user to print/download
  toast.info(
    "Please use the Print button in the receipt view to download as PDF.",
  );
};

const FeeStructure = () => {
  const [feeData, setFeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [showReceipts, setShowReceipts] = useState(false);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState("2025-26");
  const [hostedCheckoutData, setHostedCheckoutData] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      await fetchFeeData();
      await fetchReceipts();
    };
    fetchData();
  }, [selectedAcademicYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFeeData = async () => {
    try {
      const response = await getStudentFeeData(selectedAcademicYear);
      setFeeData(response.data);
    } catch (error) {
      console.error("Error fetching fee data:", error);
      if (error.response?.status === 404) {
        setFeeData(null);
      } else {
        toast.error("Failed to fetch fee data");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReceipts = async () => {
    try {
      // Always use the getStudentReceipts API for the selected academic year
      const response = await getStudentReceipts(selectedAcademicYear);
      setReceipts(response.data || []);
    } catch (error) {
      console.error("Error fetching receipts:", error);
      setReceipts([]);
    }
  };

  const handleInstallmentPayment = async (installmentNumber) => {
    setPaymentLoading(true);
    try {
      await payStudentFees(
        feeData.feeStructure._id,
        selectedAcademicYear,
        setHostedCheckoutData,
        "installment",
        installmentNumber,
      );
    } catch (error) {
      console.error("Error creating payment:", error);
      toast.error(error.response?.data?.message || "Failed to create payment");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleFullPayment = async () => {
    setPaymentLoading(true);
    try {
      await payStudentFees(
        feeData.feeStructure._id,
        selectedAcademicYear,
        setHostedCheckoutData,
        "full",
        null,
      );
    } catch (error) {
      console.error("Error creating payment:", error);
      toast.error(error.response?.data?.message || "Failed to create payment");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleViewReceipt = (receipt) => {
    setSelectedReceipt(receipt);
    setShowReceiptModal(true);
  };

  const handleCloseReceiptModal = () => {
    setSelectedReceipt(null);
    setShowReceiptModal(false);
  };

  if (loading) {
    return (
      <StudentDashboardLayout>
        <div className="flex justify-between items-center mb-6 max-w-6xl mx-auto">
          <div />
          <div className="flex gap-4 items-center">
            <select
              value={selectedAcademicYear}
              onChange={(e) => setSelectedAcademicYear(e.target.value)}
              className="px-4 py-2 border rounded-md"
            >
              <option value="2025-26">2025-26</option>
              <option value="2024-25">2024-25</option>
              <option value="2023-24">2023-24</option>
              <option value="2022-23">2022-23</option>
            </select>
            <button
              onClick={() => setShowReceipts(!showReceipts)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              {showReceipts ? "Hide Receipts" : "View Receipts"}
            </button>
            <button
              onClick={() =>
                receipts.length > 0 && downloadReceipt(receipts[0])
              }
              className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${receipts.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={receipts.length === 0}
            >
              Download Receipt
            </button>
          </div>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </StudentDashboardLayout>
    );
  }

  if (!feeData) {
    return (
      <StudentDashboardLayout>
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Fee Structure</h2>
            <div className="flex gap-4">
              <select
                value={selectedAcademicYear}
                onChange={(e) => setSelectedAcademicYear(e.target.value)}
                className="px-4 py-2 border rounded-md"
              >
                <option value="2025-26">2025-26</option>
                <option value="2024-25">2024-25</option>
                <option value="2023-24">2023-24</option>
                <option value="2022-23">2022-23</option>
              </select>
              <button
                onClick={() => setShowReceipts(!showReceipts)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                {showReceipts ? "Hide Receipts" : "View Receipts"}
              </button>
            </div>
          </div>
          <div className="text-center py-8">
            <p className="text-gray-600">
              No fee structure found for academic year {selectedAcademicYear}
            </p>
          </div>
          {/* Receipts Section */}
          {showReceipts && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold mb-4">Payment Receipts</h3>
              {receipts.length === 0 ? (
                <p className="text-gray-600">No payment receipts found.</p>
              ) : (
                <div className="space-y-4">
                  {receipts.map((receipt) => (
                    <div
                      key={receipt._id}
                      className="border rounded-lg p-4 hover:bg-gray-50"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h4 className="font-medium text-lg mb-2">
                            Receipt #{receipt.receiptNumber}
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-gray-600">Payment Date:</p>
                              <p className="font-medium">
                                {new Date(
                                  receipt.receiptDetails?.paymentDate,
                                ).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600">Amount Paid:</p>
                              <p className="font-medium text-green-600">
                                ₹{receipt.paymentSummary?.amountPaid || 0}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600">Payment Mode:</p>
                              <p className="font-medium">
                                {receipt.receiptDetails?.paymentMode ||
                                  "Online"}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600">Transaction ID:</p>
                              <p className="font-medium text-xs">
                                {receipt.receiptDetails?.transactionId || "N/A"}
                              </p>
                            </div>
                          </div>
                          {receipt.paymentSummary?.installmentNumber && (
                            <div className="mt-2">
                              <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                Installment{" "}
                                {receipt.paymentSummary.installmentNumber}
                              </span>
                            </div>
                          )}
                          {receipt.paymentSummary?.isFullPayment && (
                            <div className="mt-2">
                              <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                                Full Payment
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleViewReceipt(receipt)}
                            className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                          >
                            View & Print
                          </button>
                        </div>
                      </div>

                      {/* Fee Breakdown Preview */}
                      <div className="mt-4 p-3 bg-gray-50 rounded">
                        <h5 className="font-medium mb-2">Fee Breakdown:</h5>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className="text-gray-600">Tuition Fee: </span>
                            <span className="font-medium">
                              ₹{receipt.feeBreakdown?.tuitionFee || 0}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">
                              Development Fee:{" "}
                            </span>
                            <span className="font-medium">
                              ₹{receipt.feeBreakdown?.developmentFee || 0}
                            </span>
                          </div>
                          {receipt.feeBreakdown?.latePaymentPenalty > 0 && (
                            <div>
                              <span className="text-gray-600">
                                Late Penalty:{" "}
                              </span>
                              <span className="font-medium text-red-600">
                                ₹{receipt.feeBreakdown.latePaymentPenalty}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {/* Printable Receipt Modal */}
        {showReceiptModal && selectedReceipt && (
          <PrintableReceipt
            receipt={selectedReceipt}
            onClose={handleCloseReceiptModal}
          />
        )}
        <ToastContainer />
      </StudentDashboardLayout>
    );
  }

  return (
    <StudentDashboardLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Fee Structure</h2>
          <div className="flex gap-4">
            <select
              value={selectedAcademicYear}
              onChange={(e) => setSelectedAcademicYear(e.target.value)}
              className="px-4 py-2 border rounded-md"
            >
              <option value="2025-26">2025-26</option>
              <option value="2024-25">2024-25</option>
              <option value="2023-24">2023-24</option>
              <option value="2022-23">2022-23</option>
            </select>
            <button
              onClick={() => setShowReceipts(!showReceipts)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              {showReceipts ? "Hide Receipts" : "View Receipts"}
            </button>
          </div>
        </div>

        {/* Fee Details Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Fee Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2">Fee Breakdown</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Tuition Fee:</span>
                  <span>₹{feeData.feeStructure.fees.tuitionFee}</span>
                </div>
                <div className="flex justify-between">
                  <span>Development Fee:</span>
                  <span>₹{feeData.feeStructure.fees.developmentFee}</span>
                </div>
                <div className="flex justify-between font-bold border-t pt-2">
                  <span>Total Fee:</span>
                  <span>₹{feeData.totalAmount}</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Payment Status</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Total Amount:</span>
                  <span>₹{feeData.totalAmount}</span>
                </div>
                <div className="flex justify-between">
                  <span>Paid Amount:</span>
                  <span className="text-green-600">₹{feeData.paidAmount}</span>
                </div>
                <div className="flex justify-between">
                  <span>Remaining Balance:</span>
                  <span className="text-red-600">
                    ₹{feeData.remainingAmount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span
                    className={`font-medium ${
                      feeData.paymentStatus === "paid"
                        ? "text-green-600"
                        : feeData.paymentStatus === "partial"
                          ? "text-yellow-600"
                          : "text-red-600"
                    }`}
                  >
                    {feeData.paymentStatus.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Options */}
        {feeData.remainingAmount > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-xl font-semibold mb-4">Payment Options</h3>
            {!feeData.feeStructure.paymentAcceptance.isAcceptingPayments ? (
              <div className="text-red-600 font-medium text-center py-4">
                Fee payment is currently not allowed. Please contact the
                administration or try again later.
              </div>
            ) : (
              <>
                {/* Full Payment Option */}
                <div className="mb-4">
                  <button
                    onClick={handleFullPayment}
                    disabled={paymentLoading}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {paymentLoading
                      ? "Processing..."
                      : `Pay Full Amount (₹${feeData.remainingAmount})`}
                  </button>
                </div>

                {/* Installment Options */}
                {feeData.feeStructure.paymentStructure.allowInstallments && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Pay by Installments</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {feeData.installmentDetails.map((installment) => (
                        <div
                          key={installment.installmentNumber}
                          className={`border rounded-lg p-4 ${
                            installment.status === "paid"
                              ? "bg-green-50 border-green-200"
                              : "bg-white border-gray-200"
                          }`}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium">
                              Installment {installment.installmentNumber}
                            </span>
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                installment.status === "paid"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {installment.status.toUpperCase()}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 mb-2">
                            Amount: ₹{installment.amount}
                          </div>
                          <div className="text-sm text-gray-600 mb-3">
                            Due Date:{" "}
                            {new Date(installment.dueDate).toLocaleDateString()}
                          </div>
                          {installment.status === "pending" && (
                            <button
                              onClick={() =>
                                handleInstallmentPayment(
                                  installment.installmentNumber,
                                )
                              }
                              disabled={paymentLoading}
                              className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50"
                            >
                              {paymentLoading ? "Processing..." : "Pay Now"}
                            </button>
                          )}
                          {installment.status === "paid" &&
                            installment.paidDate && (
                              <div className="text-sm text-green-600">
                                Paid on:{" "}
                                {new Date(
                                  installment.paidDate,
                                ).toLocaleDateString()}
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Receipts Section */}
        {showReceipts && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">Payment Receipts</h3>
            {receipts.length === 0 ? (
              <p className="text-gray-600">No payment receipts found.</p>
            ) : (
              <div className="space-y-4">
                {receipts.map((receipt) => (
                  <div
                    key={receipt._id}
                    className="border rounded-lg p-4 hover:bg-gray-50"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h4 className="font-medium text-lg mb-2">
                          Receipt #{receipt.receiptNumber}
                        </h4>
                        {/* Student Name and Roll No */}
                        <div className="mb-2 text-sm">
                          <span className="font-medium">Student: </span>
                          {receipt.studentDetails?.name
                            ? `${receipt.studentDetails.name} (${
                                receipt.studentDetails.rollNo ||
                                receipt.studentDetails.rollNumber ||
                                "N/A"
                              })`
                            : receipt.studentDetails?.rollNo ||
                              receipt.studentDetails?.rollNumber ||
                              "N/A"}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Payment Date:</p>
                            <p className="font-medium">
                              {new Date(
                                receipt.receiptDetails?.paymentDate,
                              ).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Amount Paid:</p>
                            <p className="font-medium text-green-600">
                              ₹{receipt.paymentSummary?.amountPaid || 0}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Payment Mode:</p>
                            <p className="font-medium">
                              {receipt.receiptDetails?.paymentMode || "Online"}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Transaction ID:</p>
                            <p className="font-medium text-xs">
                              {receipt.receiptDetails?.transactionId || "N/A"}
                            </p>
                          </div>
                        </div>
                        {receipt.paymentSummary?.installmentNumber && (
                          <div className="mt-2">
                            <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                              Installment{" "}
                              {receipt.paymentSummary.installmentNumber}
                            </span>
                          </div>
                        )}
                        {receipt.paymentSummary?.isFullPayment && (
                          <div className="mt-2">
                            <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                              Full Payment
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleViewReceipt(receipt)}
                          className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                        >
                          View & Print
                        </button>
                        <button
                          onClick={() => downloadReceipt(receipt)}
                          className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                        >
                          Download Receipt
                        </button>
                        {/* <button
                          onClick={() => downloadReceipt(receipt)}
                          className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                        >
                          Download Receipt
                        </button> */}
                      </div>
                    </div>

                    {/* Fee Breakdown Preview */}
                    <div className="mt-4 p-3 bg-gray-50 rounded">
                      <h5 className="font-medium mb-2">Fee Breakdown:</h5>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">Tuition Fee: </span>
                          <span className="font-medium">
                            ₹{receipt.feeBreakdown?.tuitionFee || 0}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            Development Fee:{" "}
                          </span>
                          <span className="font-medium">
                            ₹{receipt.feeBreakdown?.developmentFee || 0}
                          </span>
                        </div>
                        {receipt.feeBreakdown?.latePaymentPenalty > 0 && (
                          <div>
                            <span className="text-gray-600">
                              Late Penalty:{" "}
                            </span>
                            <span className="font-medium text-red-600">
                              ₹{receipt.feeBreakdown.latePaymentPenalty}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Printable Receipt Modal */}
      {showReceiptModal && selectedReceipt && (
        <PrintableReceipt
          receipt={selectedReceipt}
          onClose={handleCloseReceiptModal}
          logoUrl={"https://dummyimage.com/150x150/cccccc/000000&text=Institution+Logo"}
        />
      )}

      {/* Hosted Checkout Form */}
      {hostedCheckoutData && (
        <RazorpayStudentFeeForm
          order_id={hostedCheckoutData.order_id}
          key_id={hostedCheckoutData.key_id}
          callback_url={hostedCheckoutData.callback_url}
          user={hostedCheckoutData.user}
          studentId={hostedCheckoutData.studentId}
          feeStructureId={hostedCheckoutData.feeStructureId}
          academicYear={hostedCheckoutData.academicYear}
          installmentNumber={hostedCheckoutData.installmentNumber}
          isFullPayment={hostedCheckoutData.isFullPayment}
        />
      )}

      <ToastContainer />
    </StudentDashboardLayout>
  );
};

export default FeeStructure;
